import { useGraphCacheStore } from '@tunasong/graph-cache'
import { logger } from '@tunasong/models'
import type { Persisted, Entity } from '@tunasong/schemas'
import type { Middleware } from 'redux'
import invariant from 'tiny-invariant'
import { entitiesApi } from '../../api/entities.js'
import { entitiesSlice } from './entities-slice.js'

// The RTK Query `updateQueryData` thunk is typed against the entitiesApi's
// root state, which we cannot reference here without creating an import cycle
// with configure-store. The runtime dispatch accepts the thunk regardless, so
// we use a local shape that just allows dispatching it.
type ThunkishDispatch = <TAction>(action: TAction) => TAction

/**
 * Sync entities actions with the cache.
 */

/**
 * When an entity arrives via `receiveEntity` (e.g. from a server push), the
 * Zustand graph-cache is updated by this middleware, but RTK Query's
 * `loadChildEntities` cache — which drives `useEntityChildren` — has no
 * mechanism to discover the new entity on its own. Patch the cached children
 * list for its parent when one exists, and invalidate the ChildEntities tag
 * to refetch any subscriber that hadn't populated a cache entry yet. The tag
 * invalidation covers cache-miss races; the patch avoids an extra round-trip
 * when the subscription is already warm.
 */
const syncChildEntitiesCache = (dispatch: ThunkishDispatch, entity: Persisted<Entity>) => {
  const parentId = entity.parentId
  if (!parentId) {
    return
  }
  dispatch(
    entitiesApi.util.updateQueryData('loadChildEntities', { parentId }, draft => {
      const idx = draft.findIndex(e => e.id === entity.id)
      if (idx >= 0) {
        draft[idx] = entity
      } else {
        draft.push(entity)
      }
    })
  )
  dispatch(entitiesApi.util.invalidateTags([{ type: 'ChildEntities', id: parentId }]))
}

const middleware: Middleware = // Most middleware do not modify the dispatch return value
  api => next => action => {
    const { partialUpdateCache, upsertCache } = useGraphCacheStore.getState()

    if (entitiesSlice.actions.receiveEntity.match(action)) {
      logger.debug('Middleware: Received entity', action.payload.entity)

      // Trigger an update of the cache.
      upsertCache(action.payload.entity)
      syncChildEntitiesCache(api.dispatch as unknown as ThunkishDispatch, action.payload.entity)
    } else if (entitiesSlice.actions.receivePartialEntity.match(action)) {
      logger.debug('Middleware: Received partial entity', action.payload)
      invariant(action.payload.id, 'Partial entity must have an id')

      partialUpdateCache(action.payload.id, action.payload)
    }
    /** Always apply the action to other reducer(s)  */
    next(action)
  }

export default middleware
